import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
  Modal,
  Nav,
  Tab,
  Spinner,
  Badge,
  Pagination as BSPagination
} from 'react-bootstrap';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet';
import {
  getCategoriesByType,
  createCategory,
  updateCategory,
  deleteCategory
} from '../services/categoryApi';
import {
  getCategoryTypes,
  createCategoryType,
  updateCategoryType,
  deleteCategoryType
} from '../services/categoryTypeApi';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import './Categories.css';

const TYPES_PER_PAGE = 10;
const CATEGORIES_PER_PAGE = 10;

const CategoriesForm = () => {
  const { user } = useAuth();
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [categoryTypes, setCategoryTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryType, setSelectedCategoryType] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [typeSearch, setTypeSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [typePage, setTypePage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);

  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryTypeId, setEditCategoryTypeId] = useState('');
  const [editCategoryProfit, setEditCategoryProfit] = useState('');
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const result = await getCategoryTypes();
      if (result.ok && result.data?.code === 200) {
        const raw = result.data.message;
        setCategoryTypes(Array.isArray(raw) ? raw : []);
      } else {
        setCategoryTypes([]);
      }
    } catch (err) {
      console.error(err);
      setCategoryTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesByType = async (typeId) => {
    setLoadingCategories(true);
    try {
      const result = await getCategoriesByType(typeId);
      if (result.ok && result.data?.code === 200) {
        const raw = result.data.message;
        setCategories(Array.isArray(raw) ? raw : []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error(err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  useEffect(() => {
    if (selectedCategoryType) loadCategoriesByType(selectedCategoryType);
    else setCategories([]);
  }, [selectedCategoryType]);

  const filteredTypes = useMemo(() => {
    const term = (typeSearch || '').toLowerCase();
    if (!term) return categoryTypes;
    return categoryTypes.filter(
      (t) => (t.categoryTypeName || '').toLowerCase().includes(term)
    );
  }, [categoryTypes, typeSearch]);

  const filteredCategories = useMemo(() => {
    const term = (categorySearch || '').toLowerCase();
    if (!term) return categories;
    return categories.filter(
      (c) =>
        (c.category_name || c.categoryName || '').toLowerCase().includes(term)
    );
  }, [categories, categorySearch]);

  const typePages = Math.max(1, Math.ceil(filteredTypes.length / TYPES_PER_PAGE));
  const paginatedTypes = useMemo(() => {
    const start = (typePage - 1) * TYPES_PER_PAGE;
    return filteredTypes.slice(start, start + TYPES_PER_PAGE);
  }, [filteredTypes, typePage]);

  const categoryPages = Math.max(1, Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE));
  const paginatedCategories = useMemo(() => {
    const start = (categoryPage - 1) * CATEGORIES_PER_PAGE;
    return filteredCategories.slice(start, start + CATEGORIES_PER_PAGE);
  }, [filteredCategories, categoryPage]);

  const handleCategoryTypeChange = (e) => {
    setSelectedCategoryType(e.target.value);
    setCategoryPage(1);
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!typeInput.trim()) {
      showAlert('Ingrese el nombre del tipo de categoría', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createCategoryType({
        firebase_UID: user.uid,
        categoryTypeName: typeInput.trim()
      });
      const data = result.data;
      if (result.ok && (data?.code === 201 || data?.code === 200)) {
        showAlert(data?.message || 'Tipo creado', 'success');
        setTypeInput('');
        loadTypes();
      } else {
        showAlert(getErrorMessage(result) || data?.message || 'Error al crear', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al crear tipo', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!categoryInput.trim()) {
      showAlert('Ingrese el nombre de la categoría', 'error');
      return;
    }
    if (!selectedCategoryType) {
      showAlert('Seleccione un tipo de categoría', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createCategory({
        firebase_UID: user.uid,
        id_category_type: selectedCategoryType,
        categoryName: categoryInput.trim()
      });
      const data = result.data;
      if (result.ok && (data?.code === 201 || data?.code === 200)) {
        showAlert(data?.message || 'Categoría creada', 'success');
        setCategoryInput('');
        loadCategoriesByType(selectedCategoryType);
      } else {
        showAlert(getErrorMessage(result) || data?.message || 'Error al crear', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al crear categoría', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditType = (type) => {
    setEditingType(type);
    setEditTypeName(type.categoryTypeName || '');
    setShowEditTypeModal(true);
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    if (!editingType?.id_category_type || !editTypeName.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await updateCategoryType(editingType.id_category_type, {
        categoryTypeName: editTypeName.trim()
      });
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Tipo actualizado', 'success');
        setShowEditTypeModal(false);
        setEditingType(null);
        loadTypes();
      } else {
        showAlert(getErrorMessage(result) || 'Error al actualizar', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al actualizar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteType = async () => {
    if (!typeToDelete?.id_category_type) return;
    setIsSubmitting(true);
    try {
      const result = await deleteCategoryType(typeToDelete.id_category_type);
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Tipo eliminado', 'success');
        setShowDeleteTypeModal(false);
        setTypeToDelete(null);
        loadTypes();
      } else {
        showAlert(getErrorMessage(result) || data?.message || 'No se pudo eliminar', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al eliminar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setEditCategoryName(cat.category_name || cat.categoryName || '');
    setEditCategoryTypeId(String(cat.id_category_type || selectedCategoryType || ''));
    setEditCategoryProfit(cat.profit != null && cat.profit !== '' ? String(cat.profit) : '');
    setShowEditCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory?.id_category || !editCategoryName.trim()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        categoryName: editCategoryName.trim(),
        id_category_type: editCategoryTypeId || undefined
      };
      const profitVal = editCategoryProfit.trim();
      if (profitVal !== '') {
        const num = parseFloat(profitVal);
        if (!Number.isNaN(num) && num >= 0) payload.profit = num;
      }
      const result = await updateCategory(editingCategory.id_category, payload);
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Categoría actualizada', 'success');
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        loadCategoriesByType(selectedCategoryType);
      } else {
        showAlert(getErrorMessage(result) || 'Error al actualizar', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al actualizar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete?.id_category) return;
    setIsSubmitting(true);
    try {
      const result = await deleteCategory(categoryToDelete.id_category);
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Categoría eliminada', 'success');
        setShowDeleteCategoryModal(false);
        setCategoryToDelete(null);
        loadCategoriesByType(selectedCategoryType);
      } else {
        showAlert(getErrorMessage(result) || data?.message || 'No se pudo eliminar', 'error');
      }
    } catch (err) {
      showAlert(err?.message || 'Error al eliminar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Configuración de categorías - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="categories-container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-folder-tree me-3"></i>
            Configuración de categorías
          </h1>
          <p className="page-subtitle">Tipos de categoría y categorías para productos y gastos</p>
        </div>

        <Tab.Container defaultActiveKey="types">
          <Nav variant="pills" className="categories-nav mb-4">
            <Nav.Item>
              <Nav.Link eventKey="types">Tipos de categoría</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="categories">Categorías</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Tab Tipos de categoría */}
            <Tab.Pane eventKey="types">
              <Card className="categories-card">
                <Card.Header className="categories-card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-layer-group me-2"></i>
                    Crear tipo de categoría
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleAddType} className="categories-form">
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nombre del tipo</Form.Label>
                          <Form.Control
                            type="text"
                            value={typeInput}
                            onChange={(e) => setTypeInput(e.target.value)}
                            placeholder="Ej: Producto, Gasto"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} className="d-flex align-items-end">
                        <Button type="submit" className="categories-btn-primary" disabled={isSubmitting}>
                          {isSubmitting ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-plus me-2"></i>Crear tipo</>}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

              <Card className="categories-card">
                <Card.Header className="categories-card-header">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Lista de tipos
                    </h5>
                    <Badge bg="primary">{filteredTypes.length} tipo{filteredTypes.length !== 1 ? 's' : ''}</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="categories-search-bar">
                    <i className="fas fa-search search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Buscar tipo..."
                      value={typeSearch}
                      onChange={(e) => { setTypeSearch(e.target.value); setTypePage(1); }}
                    />
                    {typeSearch && (
                      <button type="button" className="categories-search-clear" onClick={() => { setTypeSearch(''); setTypePage(1); }}>
                        Limpiar
                      </button>
                    )}
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : filteredTypes.length === 0 ? (
                    <div className="categories-empty">
                      <div className="empty-icon">📁</div>
                      <p className="mb-0">{categoryTypes.length === 0 ? 'No hay tipos. Cree uno arriba.' : 'No hay resultados.'}</p>
                    </div>
                  ) : (
                    <>
                      <Table hover className="categories-table" responsive>
                        <thead>
                          <tr>
                            <th>Tipo de categoría</th>
                            <th style={{ width: '120px' }} className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTypes.map((t) => (
                            <tr key={t.id_category_type}>
                              <td className="fw-semibold">{t.categoryTypeName}</td>
                              <td className="text-end">
                                <Button variant="link" className="btn-action btn-action-edit" title="Editar" onClick={() => openEditType(t)}>
                                  <i className="fas fa-edit" />
                                </Button>
                                <Button variant="link" className="btn-action btn-action-delete" title="Eliminar" onClick={() => { setTypeToDelete(t); setShowDeleteTypeModal(true); }}>
                                  <i className="fas fa-trash-alt" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {typePages > 1 && (
                        <div className="categories-pagination-wrap">
                          <small className="text-muted">Página {typePage} de {typePages}</small>
                          <BSPagination className="mb-0">
                            <BSPagination.Prev disabled={typePage === 1} onClick={() => setTypePage((p) => Math.max(1, p - 1))} />
                            {Array.from({ length: Math.min(5, typePages) }, (_, i) => {
                              let p = typePage - 2 + i;
                              if (typePages <= 5) p = i + 1;
                              else if (p < 1) p = 1;
                              else if (p > typePages) p = typePages;
                              return (
                                <BSPagination.Item key={p} active={p === typePage} onClick={() => setTypePage(p)}>{p}</BSPagination.Item>
                              );
                            })}
                            <BSPagination.Next disabled={typePage === typePages} onClick={() => setTypePage((p) => Math.min(typePages, p + 1))} />
                          </BSPagination>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* Tab Categorías */}
            <Tab.Pane eventKey="categories">
              <Card className="categories-card">
                <Card.Header className="categories-card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-folder me-2"></i>
                    Crear categoría
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleAddCategory} className="categories-form">
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tipo de categoría <span className="text-danger">*</span></Form.Label>
                          <Form.Select value={selectedCategoryType} onChange={handleCategoryTypeChange}>
                            <option value="">Seleccione un tipo...</option>
                            {categoryTypes.map((t) => (
                              <option key={t.id_category_type} value={t.id_category_type}>{t.categoryTypeName}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nombre de la categoría <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={categoryInput}
                            onChange={(e) => setCategoryInput(e.target.value)}
                            placeholder="Ej: Alimento, Accesorios"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} className="d-flex align-items-end">
                        <Button type="submit" className="categories-btn-primary" disabled={isSubmitting || !selectedCategoryType}>
                          {isSubmitting ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-plus me-2"></i>Crear categoría</>}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

              <Card className="categories-card">
                <Card.Header className="categories-card-header">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Lista de categorías
                    </h5>
                    <Badge bg="primary">{filteredCategories.length} categoría{filteredCategories.length !== 1 ? 's' : ''}</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  {!selectedCategoryType ? (
                    <div className="categories-empty">
                      <p className="mb-0 text-muted">Seleccione un tipo de categoría arriba para ver y gestionar las categorías.</p>
                    </div>
                  ) : (
                    <>
                      <div className="categories-search-bar">
                        <i className="fas fa-search search-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Buscar categoría..."
                          value={categorySearch}
                          onChange={(e) => { setCategorySearch(e.target.value); setCategoryPage(1); }}
                        />
                        {categorySearch && (
                          <button type="button" className="categories-search-clear" onClick={() => { setCategorySearch(''); setCategoryPage(1); }}>
                            Limpiar
                          </button>
                        )}
                      </div>
                      {loadingCategories ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" />
                        </div>
                      ) : filteredCategories.length === 0 ? (
                        <div className="categories-empty">
                          <div className="empty-icon">📂</div>
                          <p className="mb-0">{categories.length === 0 ? 'No hay categorías para este tipo.' : 'No hay resultados.'}</p>
                        </div>
                      ) : (
                        <>
                          <Table hover className="categories-table" responsive>
                            <thead>
                              <tr>
                                <th>Categoría</th>
                                <th>Tipo</th>
                                <th className="text-end">Utilidad (%)</th>
                                <th style={{ width: '120px' }} className="text-end">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedCategories.map((c) => (
                                <tr key={c.id_category}>
                                  <td className="fw-semibold">{c.category_name || c.categoryName}</td>
                                  <td>
                                    <Badge bg="secondary">
                                      {categoryTypes.find((t) => String(t.id_category_type) === String(c.id_category_type))?.categoryTypeName || c.id_category_type}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    <span className="categories-profit">
                                      {c.profit != null && c.profit !== ''
                                        ? `${Number(c.profit).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
                                        : '—'}
                                    </span>
                                  </td>
                                  <td className="text-end">
                                    <Button variant="link" className="btn-action btn-action-edit" title="Editar" onClick={() => openEditCategory(c)}>
                                      <i className="fas fa-edit" />
                                    </Button>
                                    <Button variant="link" className="btn-action btn-action-delete" title="Eliminar" onClick={() => { setCategoryToDelete(c); setShowDeleteCategoryModal(true); }}>
                                      <i className="fas fa-trash-alt" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {categoryPages > 1 && (
                            <div className="categories-pagination-wrap">
                              <small className="text-muted">Página {categoryPage} de {categoryPages}</small>
                              <BSPagination className="mb-0">
                                <BSPagination.Prev disabled={categoryPage === 1} onClick={() => setCategoryPage((p) => Math.max(1, p - 1))} />
                                {Array.from({ length: Math.min(5, categoryPages) }, (_, i) => {
                                  let p = categoryPage - 2 + i;
                                  if (categoryPages <= 5) p = i + 1;
                                  else if (p < 1) p = 1;
                                  else if (p > categoryPages) p = categoryPages;
                                  return (
                                    <BSPagination.Item key={p} active={p === categoryPage} onClick={() => setCategoryPage(p)}>{p}</BSPagination.Item>
                                  );
                                })}
                                <BSPagination.Next disabled={categoryPage === categoryPages} onClick={() => setCategoryPage((p) => Math.min(categoryPages, p + 1))} />
                              </BSPagination>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      {/* Modal Editar tipo */}
      <Modal show={showEditTypeModal} onHide={() => { setShowEditTypeModal(false); setEditingType(null); }} centered className="categories-modal">
        <Modal.Header closeButton>
          <Modal.Title>Editar tipo de categoría</Modal.Title>
        </Modal.Header>
        {editingType && (
          <Form onSubmit={handleSaveType}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  value={editTypeName}
                  onChange={(e) => setEditTypeName(e.target.value)}
                  required
                  className="categories-form form-control"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowEditTypeModal(false); setEditingType(null); }}>Cancelar</Button>
              <Button type="submit" className="categories-btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Guardando...' : 'Guardar'}</Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal Eliminar tipo */}
      <Modal show={showDeleteTypeModal} onHide={() => { setShowDeleteTypeModal(false); setTypeToDelete(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar tipo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar el tipo <strong>{typeToDelete?.categoryTypeName}</strong>? Si tiene categorías asociadas puede no estar permitido.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteTypeModal(false); setTypeToDelete(null); }}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteType} disabled={isSubmitting}>{isSubmitting ? 'Eliminando...' : 'Eliminar'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Editar categoría */}
      <Modal show={showEditCategoryModal} onHide={() => { setShowEditCategoryModal(false); setEditingCategory(null); setEditCategoryProfit(''); }} centered className="categories-modal">
        <Modal.Header closeButton>
          <Modal.Title>Editar categoría</Modal.Title>
        </Modal.Header>
        {editingCategory && (
          <Form onSubmit={handleSaveCategory}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  value={editCategoryName}
                  onChange={(e) => setEditCategoryName(e.target.value)}
                  required
                  className="categories-form form-control"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de categoría</Form.Label>
                <Form.Select value={editCategoryTypeId} onChange={(e) => setEditCategoryTypeId(e.target.value)} className="categories-form form-select">
                  {categoryTypes.map((t) => (
                    <option key={t.id_category_type} value={t.id_category_type}>{t.categoryTypeName}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Utilidad (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ej: 25.00"
                  value={editCategoryProfit}
                  onChange={(e) => setEditCategoryProfit(e.target.value)}
                  className="categories-form form-control"
                />
                <Form.Text className="text-muted">Porcentaje de utilidad para esta categoría (opcional).</Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowEditCategoryModal(false); setEditingCategory(null); }}>Cancelar</Button>
              <Button type="submit" className="categories-btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Guardando...' : 'Guardar'}</Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal Eliminar categoría */}
      <Modal show={showDeleteCategoryModal} onHide={() => { setShowDeleteCategoryModal(false); setCategoryToDelete(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar categoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar la categoría <strong>{categoryToDelete?.category_name || categoryToDelete?.categoryName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteCategoryModal(false); setCategoryToDelete(null); }}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteCategory} disabled={isSubmitting}>{isSubmitting ? 'Eliminando...' : 'Eliminar'}</Button>
        </Modal.Footer>
      </Modal>

      <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
    </>
  );
};

export default CategoriesForm;
